import {
  CButton,
  CButtonGroup,
  CCol,
  CFormGroup,
  CLabel,
  CRow,
} from "@coreui/react";
import { useCallback, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import {
  createItem,
  updateItem,
  ItemRequestStatus,
  getList,
} from "../../api/generics";
import Errors, { getFieldErrors } from "../../models/errors";
import { SUCCESS } from "../../utils/constants/tags";
import { FieldErrors } from "../form/FieldErrors";
import { errorAlert } from "../utils/messages";

import { RootState } from "../../store";
import { useSelector } from "react-redux";
import Asset, { newAsset } from "../../models/asset";
import AnimatedCheckbox from "../checkbox/AnimatedCheckbox";

import Document from "../../models/document";
import AssetSelect from "./AssetSelect";
import {
  listToUniqueListItems,
  UniqueListItem,
  uniqueListToListItems,
} from "../../models/unique-list-item";

interface AssetCopyFormProps {
  initialAsset?: Asset;
  initialErrors?: Errors;
  onCancel: () => void | Promise<void>;
  onSuccess: () => void | Promise<void>;
}

const AssetCopyDocumentsForm: React.FC<AssetCopyFormProps> = ({
  initialAsset,
  initialErrors,
  onCancel,
  onSuccess,
}) => {
  const company = useSelector((state: RootState) => state.company.data.company);
  const [, setLoading] = useState(true);
  const [editingAsset, setEditingAsset] = useState<Asset>(
    initialAsset ? initialAsset : newAsset()
  );

  const [currentDocuments, setCurrentDocuments] = useState<Document[]>([]);

  const [toCreateDocuments, setToCreateDocuments] = useState<
    UniqueListItem<Document>[]
  >([]);

  const [selectedParentAsset, setSelectedParentAsset] = useState<
    Asset | undefined
  >(undefined);

  const [errors, setErrors] = useState<Errors>(
    initialErrors ? initialErrors : {}
  );
  const [submitting, setSubmitting] = useState(false);

  const onParentChange = useCallback(
    async (newParent: Asset | null) => {
      setSelectedParentAsset(newParent !== null ? newParent : undefined);
      if (newParent === null) {
        setToCreateDocuments([]);
        return;
      }

      let newDocuments = await fetchAssetDocuments(newParent.id!);

      newDocuments = newDocuments.map((doc) => {
        const alreadyExists = currentDocuments.some(
          (currentDoc) => currentDoc.name === doc.name
        );

        return alreadyExists
          ? { ...doc, alreadyCreated: true }
          : { ...doc, checked: true };
      });

      const items = listToUniqueListItems(newDocuments);
      setToCreateDocuments(items);
    },
    [currentDocuments]
  );

  const onSave = async () => {
    setSubmitting(true);
    let toSendCreateDocuments = uniqueListToListItems(toCreateDocuments);
    toSendCreateDocuments = toSendCreateDocuments
      .filter((doc) => doc.checked === true && doc.alreadyCreated !== true)
      .map((doc) => {
        const { checked, alreadyCreated, ...cleanDoc } = doc;
        return cleanDoc;
      });

    // Validación adicional por si no hay documentos para enviar
    if (toSendCreateDocuments.length === 0) {
      errorAlert("Debe seleccionar al menos un documento para copiar");
      setSubmitting(false);
      return;
    }
    let toSendAsset: Asset = {
      ...editingAsset,
      documentsToCopy: toSendCreateDocuments,
    };
    let requestPromise: Promise<ItemRequestStatus<Asset>>;
    if (editingAsset.id === undefined) {
      errorAlert("Falló al obtener activo. Intente de nuevo");
      setSubmitting(false);
      return;
    } else {
      requestPromise = updateItem<Asset>(
        `/assets/copy_documents/${toSendAsset.id}/`,
        toSendAsset
      );
    }

    const assetStatus = await requestPromise;

    if (assetStatus.status !== SUCCESS) {
      if (assetStatus.errors !== undefined) {
        setErrors(assetStatus.errors);
      }

      let message = "Ha ocurrido un error!!";
      if (assetStatus.detail !== undefined) {
        message = assetStatus.detail;
      }
      errorAlert(message);
    } else {
      setErrors({});
      clearForm();
      onSuccess();
    }
    setSubmitting(false);
  };

  const onClose = () => {
    clearForm();
    onCancel();
  };

  const clearForm = () => {
    setEditingAsset(newAsset());
    setCurrentDocuments([]);
    setToCreateDocuments([]);
    setSelectedParentAsset(undefined);
  };

  const fetchAssetDocuments = async (id: number) => {
    let additionalParams: Map<string, string> | undefined = new Map<
      string,
      string
    >();

    additionalParams.set("company_id", company.id!.toString());
    additionalParams.set("asset_id", id.toString());

    const data = await getList<Document>(
      "/documents/",
      1000,
      0,
      additionalParams
    );
    if (data.data !== undefined) {
      return data.data.items;
    }
    return [];
  };

  const getInitialValues = async (id: number) => {
    setLoading(true);
    const currentDocumentsPromise = fetchAssetDocuments(id);

    const retrievedCurrentDocuments = await currentDocumentsPromise;
    setCurrentDocuments(retrievedCurrentDocuments);

    setLoading(false);
  };

  useEffect(() => {
    setEditingAsset(initialAsset ? initialAsset : newAsset());
    setSelectedParentAsset(undefined);
    setToCreateDocuments([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAsset]);

  useEffect(() => {
    getInitialValues(editingAsset.id!);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingAsset]);

  useEffect(() => {
    setErrors(initialErrors ? initialErrors : {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialErrors]);

  const hasCheckedDocuments = toCreateDocuments.some(
    (doc) => doc.item.checked === true && doc.item.alreadyCreated !== true
  );

  return (
    <>
      <fieldset disabled={submitting}>
        <CFormGroup>
          <CRow className={"mt-2"}>
            <CCol md={3}>
              <CLabel className={"mt-2"}>Copiar Documentos desde:</CLabel>
            </CCol>
            <CCol>
              <AssetSelect
                value={selectedParentAsset ? selectedParentAsset : null}
                onChange={onParentChange}
              ></AssetSelect>
              <FieldErrors
                errors={getFieldErrors("parentAssetId", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        {toCreateDocuments.length > 0 ? (
          <CFormGroup>
            <CRow className={"mt-2"}>
              <CCol md={3}>
                <CLabel>Documentos:</CLabel>
              </CCol>
              <CCol>
                {toCreateDocuments.map((doc) => {
                  const isDisabled = doc.item.alreadyCreated === true;

                  return (
                    <CRow key={doc.uuid} className={"mb-2"}>
                      <AnimatedCheckbox
                        value={doc.item.checked === true}
                        disabled={isDisabled}
                        onChange={(checked) => {
                          setToCreateDocuments((prev) =>
                            prev.map((d) =>
                              d.uuid === doc.uuid
                                ? { ...d, item: { ...d.item, checked } }
                                : d
                            )
                          );
                        }}
                      />
                      <span className={isDisabled ? "text-muted ms-2" : "ms-2"}>
                        {doc.item.name}
                        {isDisabled && " (ya fue creado)"}
                      </span>
                    </CRow>
                  );
                })}
              </CCol>
            </CRow>
          </CFormGroup>
        ) : (
          <CFormGroup>
            <CRow className={"mt-2"}>
              <CCol md={12}>
                <CLabel>
                  Seleccione un Activo desde el cual copiar los documentos:
                </CLabel>
              </CCol>
            </CRow>
          </CFormGroup>
        )}
        <CFormGroup className="float-right">
          <CButtonGroup>
            <CButton type="button" color="secondary" onClick={onClose}>
              Atras
            </CButton>
            <CButton
              type="submit"
              color="primary"
              onClick={onSave}
              disabled={
                toCreateDocuments.length === 0 ||
                !hasCheckedDocuments ||
                submitting
              }
            >
              {submitting ? (
                <Spinner
                  animation="grow"
                  style={{
                    height: "17px",
                    width: "17px",
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginRight: "10px",
                  }}
                />
              ) : (
                <></>
              )}
              {submitting ? "Guardando..." : "Guardar"}
            </CButton>
          </CButtonGroup>
        </CFormGroup>
      </fieldset>
    </>
  );
};

export default AssetCopyDocumentsForm;
